import { useQuery, type QueryKey } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useTestRunsTableRowActions } from '@/features/executions/components/useTestRunsTableRowActions';
import { queryTestRuns } from '@/features/executions/data';
import { useProjectMetricThresholds } from '@/features/projects/hooks/useProjectMetricThresholds';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import {
  buildTestDetailPath,
  buildTestRunPath,
} from '@/features/shared/components/tableSystem/tableRoutes';
import { TableV2DurationCell } from '@/features/shared/components/tableSystem/TableV2DurationCell';
import {
  TableV2MetricCells,
  TableV2MetricHeaders,
  type TableV2MetricSortField,
} from '@/features/shared/components/tableSystem/TableV2MetricColumns';
import {
  queryAllTableV2NestedRows,
  tableV2NestedCarrierRowSx,
  tableV2NestedContentSx,
  tableV2NestedDepthSx,
} from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2ActionsHeaderIcon,
  TableV2ChevronButton,
  TableV2DateTimeCell,
  TableV2NestedCollapse,
  TableV2NameCell,
  TableV2SortableHeader,
  TableV2StatusChip,
  TableV2TagGroup,
  tableV2ActionsColumnWidth,
  tableV2BodyRowHeightPx,
  tableV2ChevronCellSx,
  tableV2ChevronColumnWidth,
  tableV2NestedBodyRowHeightPx,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { TableV2QueryShell } from '@/features/shared/components/tableSystem/TableV2QueryShell';
import { formatTableV2RunNumber } from '@/features/shared/components/tableSystem/TableV2RunFormatting';
import {
  TableV2SkeletonRow,
  type TableV2SkeletonColumnKind,
} from '@/features/shared/components/tableSystem/TableV2Skeleton';
import { resolveTableV2NestedSkeletonRowCount } from '@/features/shared/components/tableSystem/TableV2SkeletonRows';
import { buildTagColorByName } from '@/features/shared/tags';
import { DeleteTestDialog } from '@/features/tests/components/dialogs/TestDialogs';
import { useTestsTableRowActions } from '@/features/tests/components/useTestsTableRowActions';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { s } from '@/theme/tokens';
import { buildEqFilter } from '@/ui/composites/grid/gridFilters';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Text } from '@/ui/primitives';
import type { TagColor, TagOption } from '@/shared/data/tags';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  MetricThresholdGroup,
  QueryResult,
  TestListItemDto,
  TestRunDto,
} from '@flowqa/shared';

type TestsTableV2Props = {
  active: boolean;
  fixedFilters?: GridQueryInput['filters'];
  forceLoading?: boolean;
  noRowsText?: string;
  queryFn: (input: GridQueryInput) => Promise<QueryResult<TestListItemDto>>;
  queryKey: QueryKey;
  search?: string;
  selectedProjectId?: string | null;
  sort?: ApiSort;
  tagOptionsOverride?: TagOption[];
  onQueryMeta?: (meta: { total: number; items: TestListItemDto[] } | null) => void;
  onSortChange?: (sort: ApiSort) => void;
};

type SortableTestField = 'displayName' | TableV2MetricSortField | 'latestRunTimestamp';
type SortableNestedTestRunField = 'durationMs' | 'runNumber' | 'status' | 'timestamp';

const defaultTestsV2Sort = { field: 'latestRunTimestamp', direction: 'desc' } satisfies ApiSort;
const defaultNestedTestRunSort = { field: 'timestamp', direction: 'desc' } satisfies ApiSort;
const skeletonColumns = [
  'chevron',
  'name',
  'status',
  'dateTime',
  'metric',
  'metric',
  'metric',
  'metric',
  'tags',
  'actions',
] satisfies TableV2SkeletonColumnKind[];
const testRunSkeletonColumns = [
  'name',
  'status',
  'duration',
  'dateTime',
  'tags',
  'actions',
] satisfies TableV2SkeletonColumnKind[];

const colgroup = (
  <colgroup>
    <col width={tableV2ChevronColumnWidth} />
    <col width="24%" />
    <col width="10%" />
    <col width="13%" />
    <col width="10%" />
    <col width="10%" />
    <col width="10%" />
    <col width="10%" />
    <col width="10%" />
    <col width={tableV2ActionsColumnWidth} />
  </colgroup>
);

const testRunColgroup = (
  <colgroup>
    <col width="30%" />
    <col width="13%" />
    <col width="13%" />
    <col width="20%" />
    <col width="20%" />
    <col width={tableV2ActionsColumnWidth} />
  </colgroup>
);

const buildTestRunHeader = ({
  sort,
  onSortChange,
}: {
  sort: ApiSort;
  onSortChange: (field: SortableNestedTestRunField) => void;
}): ReactElement => (
  <TableRow>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        field="runNumber"
        label="Test Run"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        field="status"
        label="Status"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        field="durationMs"
        label="Duration"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        field="timestamp"
        label="Ran At"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>Tags</TableCell>
    <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
      <TableV2ActionsHeaderIcon />
    </TableCell>
  </TableRow>
);

const TestRunNestedRow = ({
  deleteRowPendingId,
  row,
  updateRowTagsColorPendingId,
  onDeleteRow,
  onUpdateRowTagsColor,
}: {
  deleteRowPendingId: string | null;
  row: TestRunDto;
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: TestRunDto) => void;
  onUpdateRowTagsColor: (input: { row: TestRunDto; tags: string[]; colors: TagColor[] }) => void;
}): ReactElement => {
  const testRunNumberLabel = formatTableV2RunNumber(row.runNumber) ?? '-';
  const testRunHref = buildTestRunPath(row.testSlug ?? row.testId, row.runNumber);
  const rowName = row.displayName ?? row.testName ?? row.name ?? row.id;

  return (
    <TableRow
      sx={[
        tableV2NestedDepthSx[1].bodyRow,
        {
          '& > td': {
            borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
            height: `${String(tableV2NestedBodyRowHeightPx)}px`,
            py: s('sm'),
            verticalAlign: 'middle',
          },
        },
      ]}
    >
      <TableCell>
        <TableV2NameCell href={testRunHref} primary={testRunNumberLabel} />
      </TableCell>
      <TableCell>
        <TableV2StatusChip status={row.status} />
      </TableCell>
      <TableCell>
        <TableV2DurationCell value={row.durationMs} />
      </TableCell>
      <TableCell>
        <TableV2DateTimeCell value={row.timestamp} />
      </TableCell>
      <TableCell>
        <TableV2TagGroup tags={row.tags ?? []} />
      </TableCell>
      <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
        <TableActionsCell
          rowName={rowName}
          verticalAlign="center"
          deleteActionLabel={getEntityActionLabel('delete', 'Test Run')}
          editTagsEntityNoun="test run"
          rowTags={row.tags ?? []}
          onUpdateTagsColor={(input) => {
            onUpdateRowTagsColor({ row, ...input });
          }}
          updateTagsColorLoading={updateRowTagsColorPendingId === row.id}
          onDelete={() => {
            onDeleteRow(row);
          }}
          disableDelete={deleteRowPendingId === row.id}
        />
      </TableCell>
    </TableRow>
  );
};

const TestExpandedRuns = ({
  childCount,
  deleteRowPendingId,
  expanded,
  organisationId,
  projectId,
  testId,
  updateRowTagsColorPendingId,
  onDeleteRunRow,
  onUpdateRunRowTagsColor,
}: {
  childCount: number;
  deleteRowPendingId: string | null;
  expanded: boolean;
  organisationId: string | null;
  projectId: string;
  testId: string;
  updateRowTagsColorPendingId: string | null;
  onDeleteRunRow: (row: TestRunDto) => void;
  onUpdateRunRowTagsColor: (input: { row: TestRunDto; tags: string[]; colors: TagColor[] }) => void;
}): ReactElement => {
  const [sort, setSort] = useState<ApiSort>(defaultNestedTestRunSort);
  const handleSortChange = (field: SortableNestedTestRunField): void => {
    setSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };
  const header = buildTestRunHeader({ sort, onSortChange: handleSortChange });
  const query = useQuery({
    queryKey: queryKeys.dataGrid(
      gridPaths.testRuns,
      'v2-nested',
      organisationId,
      projectId,
      testId,
      sort.field,
      sort.direction,
    ),
    enabled: expanded,
    queryFn: async () =>
      await queryAllTableV2NestedRows(
        {
          page: 1,
          pageSize: 1,
          sorts: [sort],
          filters: buildEqFilter('testId', testId),
        },
        async (input) => await queryTestRuns(projectId, organisationId, input),
      ),
    staleTime: 30_000,
  });
  const skeletonRowCount = resolveTableV2NestedSkeletonRowCount(childCount);

  return (
    <TableV2NestedCollapse expanded={expanded}>
      <Box sx={tableV2NestedContentSx}>
        {query.isLoading ? (
          <Box sx={tableV2NestedDepthSx[1].frame}>
            <Table aria-label="Test runs loading" sx={tableV2NestedDepthSx[1].table}>
              {testRunColgroup}
              <TableHead>{header}</TableHead>
              <TableBody>
                {Array.from({ length: skeletonRowCount }, (_item, index) => (
                  <TableV2SkeletonRow
                    key={`test-runs-nested-skeleton-${String(index)}`}
                    columns={testRunSkeletonColumns}
                    isLastVisibleRow={index === skeletonRowCount - 1}
                    rowIndex={index}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : query.data && query.data.items.length > 0 ? (
          <Box sx={tableV2NestedDepthSx[1].frame}>
            <Table aria-label="Test runs" sx={tableV2NestedDepthSx[1].table}>
              {testRunColgroup}
              <TableHead>{header}</TableHead>
              <TableBody>
                {query.data.items.map((testRun) => (
                  <TestRunNestedRow
                    key={testRun.id}
                    deleteRowPendingId={deleteRowPendingId}
                    row={testRun}
                    updateRowTagsColorPendingId={updateRowTagsColorPendingId}
                    onDeleteRow={onDeleteRunRow}
                    onUpdateRowTagsColor={onUpdateRunRowTagsColor}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Text role="body" color="textSecondary">
            No test runs found.
          </Text>
        )}
      </Box>
    </TableV2NestedCollapse>
  );
};

const TestRow = ({
  deleteRowPendingId,
  isLastVisibleRow,
  row,
  tagOptions,
  thresholds,
  updateRowTagsColorPendingId,
  runDeleteRowPendingId,
  runUpdateRowTagsColorPendingId,
  onDeleteRow,
  onDeleteRunRow,
  onUpdateRowTagsColor,
  onUpdateRunRowTagsColor,
}: {
  deleteRowPendingId: string | null;
  isLastVisibleRow: boolean;
  row: TestListItemDto;
  tagOptions: TagOption[];
  thresholds: MetricThresholdGroup;
  updateRowTagsColorPendingId: string | null;
  runDeleteRowPendingId: string | null;
  runUpdateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: TestListItemDto) => void;
  onDeleteRunRow: (row: TestRunDto) => void;
  onUpdateRowTagsColor: (input: {
    row: TestListItemDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
  onUpdateRunRowTagsColor: (input: { row: TestRunDto; tags: string[]; colors: TagColor[] }) => void;
}): ReactElement => {
  const [expanded, setExpanded] = useState(false);
  const tagColorByName = buildTagColorByName(tagOptions);
  const latestRun = row.latestRun ?? null;
  const metrics = row.metrics ?? null;
  const testName = row.displayName ?? row.name;
  const suiteName = row.suites?.primary?.displayName ?? row.suites?.primary?.name ?? null;
  const testRunCount = row.executionSummaries?.length ?? row.executionStatusCounts?.total ?? 0;

  return (
    <>
      <TableRow
        sx={{
          backgroundColor: 'background.paper',
          '& > td': {
            borderBottom:
              expanded || isLastVisibleRow ? 0 : (theme) => `1px solid ${theme.app.colors.border}`,
            height: `${String(tableV2BodyRowHeightPx)}px`,
            py: s('md'),
            verticalAlign: 'middle',
          },
        }}
      >
        <TableCell align="center" sx={tableV2ChevronCellSx}>
          <TableV2ChevronButton
            expanded={expanded}
            label={expanded ? 'Collapse test runs' : 'Expand test runs'}
            onClick={() => {
              setExpanded((current) => !current);
            }}
          />
        </TableCell>
        <TableCell>
          <TableV2NameCell
            href={buildTestDetailPath(row.slug)}
            primary={testName}
            secondary={suiteName}
          />
        </TableCell>
        <TableCell>
          <TableV2StatusChip status={latestRun?.status ?? null} />
        </TableCell>
        <TableCell>
          <TableV2DateTimeCell value={latestRun?.timestamp ?? null} />
        </TableCell>
        <TableV2MetricCells metrics={metrics} thresholds={thresholds} />
        <TableCell>
          <TableV2TagGroup tagColorByName={tagColorByName} tags={row.tags ?? []} />
        </TableCell>
        <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
          <TableActionsCell
            rowName={testName}
            verticalAlign="center"
            deleteActionLabel={getEntityActionLabel('delete', 'Test')}
            editTagsEntityNoun="test"
            rowTags={row.tags ?? []}
            onUpdateTagsColor={(input) => {
              onUpdateRowTagsColor({ row, ...input });
            }}
            updateTagsColorLoading={updateRowTagsColorPendingId === row.id}
            onDelete={() => {
              onDeleteRow(row);
            }}
            disableDelete={deleteRowPendingId === row.id}
          />
        </TableCell>
      </TableRow>
      <TableRow
        sx={[
          tableV2NestedCarrierRowSx,
          expanded && !isLastVisibleRow
            ? {
                '& > td': {
                  borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
                },
              }
            : {},
        ]}
      >
        <TableCell colSpan={skeletonColumns.length}>
          <TestExpandedRuns
            childCount={testRunCount}
            deleteRowPendingId={runDeleteRowPendingId}
            expanded={expanded}
            organisationId={row.organisationId}
            projectId={row.projectId}
            testId={row.id}
            updateRowTagsColorPendingId={runUpdateRowTagsColorPendingId}
            onDeleteRunRow={onDeleteRunRow}
            onUpdateRunRowTagsColor={onUpdateRunRowTagsColor}
          />
        </TableCell>
      </TableRow>
    </>
  );
};

export const TestsTableV2 = ({
  active,
  fixedFilters,
  forceLoading = false,
  noRowsText = 'No data',
  onQueryMeta,
  queryFn,
  queryKey,
  search,
  selectedProjectId = null,
  sort: controlledSort,
  tagOptionsOverride = [],
  onSortChange,
}: TestsTableV2Props): ReactElement => {
  const [localSort, setLocalSort] = useState<ApiSort>(defaultTestsV2Sort);
  const sort = controlledSort ?? localSort;
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const {
    deleteLoading,
    deleteRowPendingId,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId,
  } = useTestsTableRowActions({ selectedProjectId });
  const {
    deleteLoading: runDeleteLoading,
    deleteRowPendingId: runDeleteRowPendingId,
    deleteTarget: runDeleteTarget,
    onDeleteClose: onRunDeleteClose,
    onDeleteConfirm: onRunDeleteConfirm,
    onDeleteRow: onDeleteRunRow,
    onUpdateRowTagsColor: onUpdateRunRowTagsColor,
    updateRowTagsColorPendingId: runUpdateRowTagsColorPendingId,
  } = useTestRunsTableRowActions({ selectedProjectId });

  const handleSortChange = (field: SortableTestField): void => {
    const nextSort: ApiSort = {
      field,
      direction: sort.field === field && sort.direction === 'desc' ? 'asc' : 'desc',
    };
    setLocalSort(nextSort);
    onSortChange?.(nextSort);
  };

  const header = (
    <TableRow>
      <TableCell sx={{ p: s('none'), width: tableV2ChevronColumnWidth }} />
      <TableCell>
        <TableV2SortableHeader
          activeSort={sort}
          field="displayName"
          label="Test"
          onSortChange={handleSortChange}
        />
      </TableCell>
      <TableCell>Latest Status</TableCell>
      <TableCell>
        <TableV2SortableHeader
          activeSort={sort}
          field="latestRunTimestamp"
          label="Ran At"
          onSortChange={handleSortChange}
        />
      </TableCell>
      <TableV2MetricHeaders sort={sort} onSortChange={handleSortChange} />
      <TableCell>Tags</TableCell>
      <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
        <TableV2ActionsHeaderIcon />
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <TableV2QueryShell
        active={active}
        ariaLabel="Tests"
        colgroup={colgroup}
        emptyText={noRowsText}
        entityLabel="tests"
        fixedFilters={fixedFilters}
        forceLoading={forceLoading}
        header={header}
        queryFn={queryFn}
        queryKey={queryKey}
        search={search}
        skeletonColumns={skeletonColumns}
        sorts={[sort]}
        onQueryMeta={onQueryMeta}
        renderRow={(row, context) => (
          <TestRow
            key={row.id}
            deleteRowPendingId={deleteRowPendingId}
            isLastVisibleRow={context.isLastVisibleRow}
            row={row}
            runDeleteRowPendingId={runDeleteRowPendingId}
            runUpdateRowTagsColorPendingId={runUpdateRowTagsColorPendingId}
            tagOptions={tagOptionsOverride}
            thresholds={thresholds.tests}
            updateRowTagsColorPendingId={updateRowTagsColorPendingId}
            onDeleteRow={onDeleteRow}
            onDeleteRunRow={onDeleteRunRow}
            onUpdateRowTagsColor={onUpdateRowTagsColor}
            onUpdateRunRowTagsColor={onUpdateRunRowTagsColor}
          />
        )}
      />
      <DeleteTestDialog
        open={deleteTarget !== null}
        testName={deleteTarget?.name}
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
      <ConfirmDialog
        open={runDeleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'testRun' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'testRun',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'testRun',
        })}
        confirmColor="error"
        loading={runDeleteLoading}
        onClose={onRunDeleteClose}
        onConfirm={onRunDeleteConfirm}
      />
    </>
  );
};
